/**
 * Embed entry point — loads the builder into any third-party page via a
 * single script tag. Tenant identity is resolved automatically from the
 * requesting page's domain (no parameters required), provided the domain
 * is registered in the tenant's Embed Domains whitelist in the admin.
 *
 * Usage:
 *   <script src="https://yourserver.com/embed.js"
 *           data-api-url="https://yourserver.com"
 *           data-container="#my-builder">
 *   </script>
 *
 * Attributes:
 *   data-api-url   (required for cross-origin) — absolute base URL of the
 *                  API server, e.g. "https://builds.yourcompany.com"
 *   data-container (optional) — CSS selector for the mount element.
 *                  Created automatically if not found in the DOM.
 *
 * Domain resolution:
 *   When the embed script runs on https://clientsite.com, the browser
 *   sends Origin: https://clientsite.com with every API request. The
 *   server matches that hostname against the tenant's embed_domains
 *   whitelist and resolves the correct tenant automatically — no
 *   tenant ID is needed in the URL or script tag.
 *
 * iframe alternative (still supported):
 *   <iframe src="https://yourserver.com/" width="100%" height="800"></iframe>
 *   Works the same way via Origin header domain resolution.
 */

(function () {
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script) return;

  // Absolute API server URL — required when serving from a different origin.
  // Strip trailing slashes so we can safely append "/api/v1/..." paths.
  const apiUrl = (script.dataset.apiUrl ?? '').replace(/\/+$/, '');
  if (apiUrl) {
    (window as any).__VC_API_URL__ = apiUrl;
  }

  const containerId = script.dataset.container || '#vehicle-customizer';

  // Create mount container if it doesn't already exist in the DOM
  let container = document.querySelector(containerId);
  if (!container) {
    container = document.createElement('div');
    container.id = containerId.replace(/^[#.]/, '');
    script.parentElement?.insertBefore(container, script);
  }

  // Inject the main builder module as a <script type="module"> with an
  // absolute URL derived from this script's own src. A plain dynamic
  // import('./assets/main.js') would fail cross-origin because classic
  // scripts loaded from a foreign origin get base URL "about:blank".
  const base = script.src.replace(/\/[^\/]*$/, '/');

  // Clear any stale tenant ID cached in localStorage from a previous session
  // or from a ?tenant= URL param on this domain. In embed mode the server
  // resolves the correct tenant from the Origin header — a stale cached ID
  // would override that and return the wrong tenant's data.
  localStorage.removeItem('vc_tenant_id');

  // Inject the stylesheet — main.css is only linked via index.html so it won't
  // load when assets/main.js is fetched directly on a third-party page.
  const cssLink = document.createElement('link');
  cssLink.rel = 'stylesheet';
  cssLink.href = base + 'assets/main.css';
  document.head.appendChild(cssLink);

  const loader = document.createElement('script');
  loader.type = 'module';
  loader.src = base + 'assets/main.js';
  document.head.appendChild(loader);
})();
